import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../contexts/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { AgendaCompany, ComByUserIdQuery, ComByUserIdQueryVariables, GetAgendaUserQuery, GetAgendaUserQueryVariables, UpdateAgendaCompanyInput, UpdateAgendaCompanyMutation, UpdateAgendaCompanyMutationVariables } from "../../graphql/API";
import { comByUserId, getAgendaUser, updateAgendaCompany } from "./queries";
import CustomModal from "../../components/Modals/CustomModal";
import { useEffect, useState } from "react";
import Sidebar from "../services/Sidebar";
import { Button, Col, Form, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import UserImage from "../../components/UserImage";
import { Input } from "@aws-amplify/ui-react";
import CustomInput from "./CustomInput";
import { useForm } from "react-hook-form";
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidV4 } from 'uuid';
import CompanyLogo from "../../components/CompanyLogo";

export type IEditableCompanyField = 'name' | 'address' | 'phoneNumber';
export type IEditableCompany = Pick<AgendaCompany, IEditableCompanyField>;

const CompanyScreen = () => {
    // AUTH
    const { userId: AuthUserId } = useAuthContext();
    // I18N
    const { t } = useTranslation();
    // TOAST
    const [showToast, setShowToast] = useState(false);
    const [toastStatus, setToastStatus] = useState("");
    const [toastTitle, setToastTitle] = useState("");
    const [toastMessage, setToastMessage] = useState("");
    // MODAL
    const [contentModal, setContentModal] = useState(null);
    const [linkModal, setLinkModal] = useState(null);
    const [showCustomModal, setShowCustomModal] = useState(false);
    // FILE
    const [file, setFile] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // NAVIGATION
    const location = useLocation();
    const navigate = useNavigate();

    const {control, handleSubmit, setValue, register, formState: { errors }} = useForm<IEditableCompany>();

    const {data: dataUser, loading: loadingUser, error: errorUser, refetch: refetchUser, fetchMore} = useQuery
          <GetAgendaUserQuery, GetAgendaUserQueryVariables>(getAgendaUser, {variables: {userId: AuthUserId}});

    const { data, loading } = useQuery<ComByUserIdQuery, ComByUserIdQueryVariables>(comByUserId, {
        variables: { userId: AuthUserId },});

    const [doUpdateCompany, {loading: loadingUpdate, error: errorUpdate}] = 
          useMutation<UpdateAgendaCompanyMutation, UpdateAgendaCompanyMutationVariables>(updateAgendaCompany);

    const user = dataUser?.getAgendaUser;
    const company = data?.comByUserId?.items[0];

    useEffect(() => {
        if (company) {
            // Initialise les champs du formulaire avec les valeurs de l'objet company
            setValue('name', company.name || '');
            setValue('address', company.address || '');
            setValue('phoneNumber', company.phoneNumber || '');
        }
    }, [company, setValue]);

    //Apollo
    if (loading || loadingUser) {
        return (
            <div className="spinnerContainer">
                <Spinner animation="border" role="status" className="spinner">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>   
            </div>
        )
    };

    if (errorUser || errorUpdate ) { //|| errorDelete) {
        return (
            <div className="users-container flex-grow-1 p-3">
                <h3>Error fetching the user</h3>
                <p>{errorUser?.message || "User not found"}</p>
                <Link to="" onClick={() => window.location.reload()}>Reload</Link>
            </div>
        );
    };

    if (user?.userId != user?.com?.userId) navigate(t('link.dashboard'), { state: { status: "bg-danger", title: t("toast.companyRestrictedTitle"), toastMessage: t('toast.companyRestricted')}});

    const onSubmit = async (formData: IEditableCompany) => {
        const input: UpdateAgendaCompanyInput = {comId: user?.comId, ...formData};
        if (file) {
            input.image = await uploadMedia(file);
        };
        // console.log('formData', formData);
        if (user.username) {
            // if (await validateUsername(formData.username)){    
                // console.log('formData:', formData);
                // console.log(selectedLangage + " ... " + user?.language);
                if (file) {      
                    const result = await doUpdateCompany({variables: {input: {comId: user?.comId, ...formData}}});
                    // RNRestart.restart(); // reload app
                    // console.log('result with logo:', result);
                } else {
                    
                    const result = await doUpdateCompany({variables: {input: {comId: user?.comId, ...formData}}});
                    // console.log('result:', result);
                }
                // navigation.popToTop();
                // navigation.goBack();
                // navigate(t('link.settings'));
                navigate(t('link.dashboard'), { state: { status: "bg-success", title: "", toastMessage: t('toast.editCompany') }});
                // window.location.reload();
            // };
        } else {
            alert(t('signup.waring') + " :" + t('profile.provideUsername'));
        }
    };

    const convertToJpgBlob = (imageFile: any) => {
        return new Promise<Blob | null>((resolve) => {
            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
                const result = event.target?.result;
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0);
                    }

                    // Exportation du canvas vers un Blob JPEG.
                    // Le deuxième argument est la qualité (0.0 à 1.0)
                    canvas.toBlob((blob) => {
                        resolve(blob);
                    }, 'image/jpeg', 0.9); // 0.9 = 90% de qualité JPEG

                    // On s'assure de libérer l'URL de l'image
                    URL.revokeObjectURL(img.src);
                };

                // FileReader.result can be string (data URL) or ArrayBuffer — handle both safely
                if (typeof result === 'string') {
                    img.src = result;
                } else if (result instanceof ArrayBuffer) {
                    const blobFromArray = new Blob([result]);
                    img.src = URL.createObjectURL(blobFromArray);
                } else {
                    // Unexpected result type or null
                    resolve(null);
                }
            };
            // Lire le fichier comme une URL de données
            reader.readAsDataURL(imageFile);
        });
    };

    const uploadMedia = async (data: File) => {
        try {
            const jpgBlob = await convertToJpgBlob(data);
            const jpgFile = new File([jpgBlob], `${data.name.split('.')[0]}.jpg`, { type: 'image/jpeg' });

            // const fileName = `${uuidV4()}.${data.name.split('.').pop()}`;
            const uriParts = file.name.split('.');
            const extension = uriParts[uriParts.length - 1];
            const fileName = 'logo.jpg'; //`${uuidV4()}.${extension}`;
            const name = file.name;
            const type = file.type;
            const key = `${user?.comId}/logo/${fileName}`;
            const result = await uploadData({
                // key: fileName,//`${uuidV4()}.${extension}`,
                path: `public/${key}`,
                // name: file.name,
                data: file,
                options: ({
                    // 💡 Ceci dit au navigateur de considérer le cache comme périmé immédiatement.
                    // Le navigateur DOIT ensuite revalider l'objet auprès de S3, 
                    // recevant ainsi les en-têtes CORS mis à jour à chaque fois.
                    CacheControl: 'max-age=0, must-revalidate', 
                } as any)
            }).result;
            return key;
        } catch (error) {
            alert(t('profile.error_uploading_image') + " :" + (error as Error).message);
        };
    };

    const onChangePicture = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setFile(file);
            setSelectedPhoto(file);
            // Génère une URL pour afficher l'aperçu
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    return (
        <>
        <CustomModal
            show={showCustomModal}
            onHide={() => setShowCustomModal(false)}
            link={linkModal}
            children={contentModal}
            // animation={true}
        />
        <ToastContainer className="p-3 toastTop">
            <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide className={`${toastStatus}`}>
                { toastTitle != "" &&
                <Toast.Header className={`text-center ${toastStatus}`}>
                    <strong className="mx-auto text-white-50  ">{toastTitle}</strong>
                </Toast.Header>
                }
                <Toast.Body className="text-center"><strong className="mx-auto text-white text-center">{toastMessage}</strong></Toast.Body>
            </Toast>
        </ToastContainer>
        <div className="d-flex">
            <Sidebar user={user} />
            <div className="users-container flex-grow-1 p-3">
                <Row className="g-3 bg-white p-3 rounded-4 shadow-sm hover:shadow-md mt-2">
                {/* Première colonne : Photo et heures */}
                    <Col xs={12} md={6} className="d-flex flex-column align-items-center w-100 border-bottom mt-2 pb-2">
                        <Row className="text-end w-100">
                            <h3>{company?.name}</h3>
                        </Row>
                    </Col>
                    <Col className="d-flex flex-column">
                        <div className="p-2">
                            <Form onSubmit={handleSubmit(onSubmit)} className="mt-3">
                                <div className="bg-white p-4 d-flex flex-column align-items-center gap-3 rounded-4 shadow-sm hover:shadow-md">
                                    <div className="form_wrapper m-0 m-auto">
                                        { previewUrl ? 
                                            <img src={previewUrl} alt="Aperçu" className="comLogo_lg" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                                        : 
                                            <CompanyLogo comId={user?.comId} customClass="comLogo_lg"/>
                                        }
                        
                                        <Input type="file" title={t('document.selectFile')} onChange={onChangePicture} className="mt-4 mb-4 form-control" /> 

                                        <CustomInput 
                                            name="name" 
                                            control={control} 
                                            rules={{required: t('company.nameRequired'), minLength: { value: 3, message: t('company.nameRequiredMessage')}}} 
                                            label={t('company.name')}
                                            startPlaceholder={t('company.startPlaceholder')}
                                            stopPlaceholder={t('company.stopPlaceholder')}
                                        />

                                        <CustomInput 
                                            name="phoneNumber" 
                                            control={control} 
                                            rules={{required: t('company.phoneNumberRequired'), 
                                                pattern: {
                                                    value: /^\d{3}-\d{3}-\d{4}$/,
                                                    message: t('company.phoneFormatError') // Nouveau message d'erreur
                                                },
                                                minLength: { value: 12, message: t('company.phoneNumberRequiredMessage'),
                                                }}} 
                                            label={t('company.phone')}
                                            startPlaceholder={t('company.startPlaceholder')}
                                            stopPlaceholder={t('company.stopPlaceholder')}
                                        />

                                        <CustomInput 
                                            name="address" 
                                            control={control} 
                                            rules={{required: t('company.addressRequired'), minLength: { value: 3, message: t('company.addressRequiredMessage')}}} 
                                            label={t('company.address')}
                                            startPlaceholder={t('company.startPlaceholder')}
                                            stopPlaceholder={t('company.stopPlaceholder')}
                                        />
                                        {/* <div className="p-3 float-start">
                                            <span>{company.address}</span>
                                        </div> */}
                                        <Button type="submit" >{loadingUpdate ? t('profile.submitting') : t('profile.submit')}</Button>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
        </>
    );

}
export default CompanyScreen;